// src/components/OnboardingWizard.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  SiInstagram,
  SiTiktok,
  SiYoutube,
  SiFacebook,
  SiLinktree,
  SiSnapchat,
} from 'react-icons/si';
import { FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa';
import styles from '../styles/GetStarted.module.css';

const INITIAL_FORM = {
  name: '',
  phone: '',
  email: '',
  services: { facebook: false, snapchat: false },
  hasFbPage: '',
  fbPageName: '',
  fbUsernames: [''],
  contentSources: [{ platform: 'Instagram', handle: '' }],
  promoLink: '',
  autoResponse: '',
  longForm: { yt: false, pod: false, live: false, none: false },
  shortLib: '',
  revenue: '',
  hasExtraContent: '',
  extraContentLink: '',
  businessName: '',
  businessEmail: '',
  isReferral: '',
  referredBy: '',
};

function buildInitialForm({ referralUsername, skipServiceStep, initialService }) {
  const f = { ...INITIAL_FORM };
  if (referralUsername?.trim()) {
    f.isReferral = 'Yes';
    f.referredBy = referralUsername.trim();
  }
  if (skipServiceStep && (initialService === 'facebook' || initialService === 'snapchat')) {
    f.services = {
      facebook: initialService === 'facebook',
      snapchat: initialService === 'snapchat',
    };
  }
  return f;
}

/**
 * @param {'page'|'overlay'|'embedded'} variant
 * @param {string|null} referralUsername — affiliate referrer username (from ?r= on parent routes)
 * @param {boolean} skipServiceStep — when true, service was chosen on the landing hero; omit that step
 * @param {'facebook'|'snapchat'|null} initialService — pre-selects services.* when skipServiceStep
 * @param {() => void} [onClose] — overlay / embedded: dismiss or return to path selection
 */
export default function OnboardingWizard({
  variant = 'page',
  referralUsername = null,
  skipServiceStep = false,
  initialService = null,
  onClose,
}) {
  const isEmbedded = variant === 'embedded';
  const isOverlay = variant === 'overlay';
  const embeddedTopRef = useRef(null);
  const [form, setForm] = useState(() =>
    buildInitialForm({ referralUsername, skipServiceStep, initialService })
  );

  const [stepIndex, setStepIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const API_URL = 'https://sucrbe1r7d.execute-api.us-east-1.amazonaws.com/Prod/onboarding';
  const AFFILIATE_API_URL = 'https://rody753ay0.execute-api.us-east-1.amazonaws.com/dev';

  useEffect(() => {
    if (!referralUsername?.trim()) return;
    setForm((prev) => ({
      ...prev,
      isReferral: 'Yes',
      referredBy: referralUsername.trim(),
    }));
  }, [referralUsername]);

  useEffect(() => {
    if (!isOverlay && !isEmbedded) return;
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isOverlay, isEmbedded]);

  useEffect(() => {
    if (!isEmbedded) return;
    if (!embeddedTopRef.current) return;
    embeddedTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [isEmbedded, stepIndex]);

  // ─── Countdown effect (standalone /getstarted page only) ─────────────
  useEffect(() => {
    if (!submitted || isEmbedded) return;
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
    window.location.href = '/';
  }, [submitted, countdown, isEmbedded]);

  // ─── Handlers ─────────────────────────────────────────────────────────
  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const updateFbUsername = (idx, value) => {
    setForm(prev => {
      const arr = [...prev.fbUsernames];
      arr[idx] = value;
      return { ...prev, fbUsernames: arr };
    });
  };

  const addFbUsername = () => {
    setForm(prev => ({ ...prev, fbUsernames: [...prev.fbUsernames, ''] }));
  };

  const removeFbUsername = idx => {
    setForm(prev => {
      const arr = [...prev.fbUsernames];
      arr.splice(idx, 1);
      return { ...prev, fbUsernames: arr };
    });
  };

  const updateContentSource = (idx, key, value) => {
    setForm(prev => {
      const arr = prev.contentSources.map((entry, i) =>
        i === idx ? { ...entry, [key]: value } : entry
      );
      return { ...prev, contentSources: arr };
    });
  };

  const addContentSource = () => {
    setForm(prev => ({
      ...prev,
      contentSources: [...prev.contentSources, { platform: 'Instagram', handle: '' }],
    }));
  };

  const removeContentSource = idx => {
    setForm(prev => {
      const arr = [...prev.contentSources];
      arr.splice(idx, 1);
      return { ...prev, contentSources: arr };
    });
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      // First, submit the main onboarding form
      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to save data');
      }
      await response.json();

      // If this is a referral, add to affiliate system
      if (referralUsername) {
        try {
          // Determine Facebook page name to use
          let facebookPageName = '';
          if (form.hasFbPage === 'Yes' && form.fbPageName.trim()) {
            facebookPageName = form.fbPageName.trim();
          } else if (form.name.trim()) {
            // Use person's name if no Facebook page name specified
            facebookPageName = `${form.name.trim()}'s Page`;
          } else {
            facebookPageName = 'New Customer Page';
          }

          // Call affiliate API to add referral
          const affiliateResponse = await fetch(
            `${AFFILIATE_API_URL}/affiliate/username/${referralUsername}/referral`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                facebook_page_name: facebookPageName
              }),
            }
          );

          if (!affiliateResponse.ok) {
            // Log the error but don't fail the whole submission
            console.warn('Failed to add affiliate referral:', await affiliateResponse.text());
          } else {
            console.log('Successfully added affiliate referral');
          }
        } catch (affiliateError) {
          // Log the error but don't fail the whole submission
          console.warn('Error adding affiliate referral:', affiliateError);
        }
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert(`❌ Error: ${error.message}`);
    } finally {
      setSending(false);
    }
  };

  const OptionGroup = ({ options, selected, onChange, singleSelect = true }) => {
    return (
      <div className={styles.optionGroup}>
        {options.map((opt) => {
          const isSelected = singleSelect ? selected === opt : selected.includes(opt);
          return (
            <div
              key={opt}
              className={`${styles.optionButton} ${isSelected ? styles.selected : ''}`}
              onClick={() => {
                if (singleSelect) {
                  onChange(opt);
                } else if (isSelected) {
                  onChange(selected.filter((item) => item !== opt));
                } else {
                  onChange([...selected, opt]);
                }
              }}
            >
              {opt}
            </div>
          );
        })}
      </div>
    );
  };

  // ─── map platform key → icon component (stable ref for step definitions) ─
  const platformIconMap = useMemo(
    () => ({
      Instagram: <SiInstagram size={20} />,
      TikTok: <SiTiktok size={20} />,
      YouTube: <SiYoutube size={20} />,
      Facebook: <SiFacebook size={20} />,
      Other: <SiLinktree size={20} />,
    }),
    []
  );

  // ─── Step definitions with validation ─────────────────────────────────
  const allSteps = useMemo(() => {
    const longFormSelected =
      form.longForm.yt || form.longForm.pod || form.longForm.live || form.longForm.none;

    const steps = [
      {
        id: 'contactInfo',
        isComplete: () =>
          form.name.trim() !== '' && (form.phone.trim() !== '' || form.email.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="contactInfo">
            <legend>
              Your Contact Information<span className={styles.required}>*</span>
            </legend>
            <div className={styles.formGrid}>
              <label className={styles.spanAll}>
                Your Name:
                <input
                  className={styles.input}
                  placeholder="Full Name"
                  value={form.name}
                  onChange={e => updateField('name', e.target.value.trimStart())}
                />
              </label>
              <label>
                Phone Number:
                <input
                  type="tel"
                  className={styles.input}
                  placeholder="e.g. 123-456-7890"
                  value={form.phone}
                  onChange={e => updateField('phone', e.target.value.trim())}
                />
              </label>
              <label>
                Email Address:
                <input
                  type="email"
                  className={styles.input}
                  placeholder="e.g. you@example.com"
                  value={form.email}
                  onChange={e => updateField('email', e.target.value.trim())}
                />
              </label>
            </div>
          </fieldset>
        ),
      },
      {
        id: 'services',
        isComplete: () => form.services.facebook || form.services.snapchat,
        render: () => {
          const options = [
            {
              id: 'facebook',
              label: 'Facebook Page Management',
              description: "We'll help manage and grow your Facebook presence",
              icon: <SiFacebook size={24} />,
            },
            {
              id: 'snapchat',
              label: 'Snapchat Star Acquisition',
              description: "We'll help you become a Snapchat Star",
              icon: <SiSnapchat size={24} />,
            },
          ];

          const handleServiceSelect = (serviceId) => {
            const newServices = { facebook: false, snapchat: false };
            newServices[serviceId] = true;
            setForm(prev => ({ ...prev, services: newServices }));
          };

          return (
            <fieldset className={styles.step} key="services">
              <legend>
                Select Service<span className={styles.required}>*</span>
              </legend>
              <div className={styles.serviceOptions}>
                {options.map(opt => {
                  const isSelected = form.services[opt.id];
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`${styles.serviceOption} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleServiceSelect(opt.id)}
                      aria-pressed={isSelected}
                    >
                      <div className={styles.serviceIcon}>{opt.icon}</div>
                      <div className={styles.serviceContent}>
                        <div className={styles.serviceLabel}>{opt.label}</div>
                        <div className={styles.serviceDescription}>{opt.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        },
      },
      {
        id: 'facebookSetup',
        isComplete: () => {
          if (!(form.hasFbPage === 'Yes' || form.hasFbPage === 'No')) return false;
          if (form.hasFbPage === 'No') return true;
          return (
            form.fbPageName.trim() !== '' &&
            form.fbUsernames.length > 0 &&
            form.fbUsernames.every(u => u.trim() !== '')
          );
        },
        render: () => (
          <fieldset className={styles.step} key="facebookSetup">
            <legend>
              Facebook Setup<span className={styles.required}>*</span>
            </legend>
            <label>
              Do you already have a Facebook Page?
              <OptionGroup
                options={['Yes', 'No']}
                selected={form.hasFbPage}
                onChange={val => updateField('hasFbPage', val)}
              />
            </label>
            {form.hasFbPage === 'Yes' && (
              <div className={styles.groupBlock}>
                <label>
                  Facebook Page Name:
                  <input
                    className={styles.input}
                    placeholder="Exact Page Name"
                    value={form.fbPageName}
                    onChange={e => updateField('fbPageName', e.target.value.trimStart())}
                  />
                </label>
                <label className={styles.compactLabel}>
                  Facebook Username(s) <span className={styles.hintInline}>(add more if taken)</span>
                </label>
                {form.fbUsernames.map((u, idx) => (
                  <div key={idx} className={styles.platformRow}>
                    <input
                      className={styles.inputHalf}
                      placeholder="facebook.com/username"
                      value={u}
                      onChange={e => updateFbUsername(idx, e.target.value.trim())}
                    />
                    {form.fbUsernames.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFbUsername(idx)}
                        className={styles.removeBtn}
                        aria-label="Remove this username"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" onClick={addFbUsername} className={styles.smallLink}>
                  + Add another username
                </button>
              </div>
            )}
          </fieldset>
        ),
      },
      {
        id: 'contentAndPromotion',
        isComplete: () =>
          form.contentSources.length > 0 &&
          form.contentSources.every(cs => cs.handle.trim() !== '') &&
          form.promoLink.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="contentAndPromotion">
            <legend>
              Content Sources & Promotion<span className={styles.required}>*</span>
            </legend>
            <label className={styles.compactLabel}>Where can we pull content from?</label>
            <div className={styles.platformList}>
              {form.contentSources.map((entry, idx) => (
                <div key={idx} className={styles.platformRow}>
                  <div className={styles.platformIconContainer}>
                    {platformIconMap[entry.platform]}
                  </div>
                  <select
                    className={styles.platformSelect}
                    value={entry.platform}
                    onChange={e => updateContentSource(idx, 'platform', e.target.value)}
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    className={styles.inputInline}
                    placeholder={`${entry.platform} handle`}
                    value={entry.handle}
                    onChange={e => updateContentSource(idx, 'handle', e.target.value.trim())}
                  />
                  {form.contentSources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContentSource(idx)}
                      className={styles.removeBtn}
                      aria-label="Remove this platform entry"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addContentSource} className={styles.smallLink}>
              <span className={styles.plusIcon}>+</span>
              <span>Add another platform</span>
            </button>
            <div className={styles.groupBlock}>
              <label>
                Link to Promote:
                <input
                  className={styles.input}
                  placeholder="Full URL"
                  value={form.promoLink}
                  onChange={e => updateField('promoLink', e.target.value.trim())}
                />
              </label>
              <label>
                Auto response (optional):
                <textarea
                  className={styles.textarea}
                  placeholder="Paste your auto-reply here"
                  value={form.autoResponse}
                  onChange={e => updateField('autoResponse', e.target.value)}
                />
              </label>
            </div>
          </fieldset>
        ),
      },
      {
        id: 'contentProfile',
        isComplete: () => longFormSelected && form.shortLib !== '' && form.revenue !== '',
        render: () => {
          const opts = [
            { key: 'yt', label: 'YouTube' },
            { key: 'pod', label: 'Podcasts' },
            { key: 'live', label: 'Live Streams' },
            { key: 'none', label: 'None of the Above' },
          ];
          return (
            <fieldset className={styles.step} key="contentProfile">
              <legend>
                Content Profile<span className={styles.required}>*</span>
              </legend>
              <label className={styles.compactLabel}>Do you create long-form content?</label>
              <div className={styles.optionGroupMulti}>
                {opts.map(opt => {
                  const isSelected = form.longForm[opt.key];
                  return (
                    <div
                      key={opt.key}
                      className={`${styles.toggleButton} ${isSelected ? styles.toggleSelected : ''}`}
                      onClick={() => {
                        if (opt.key === 'none') {
                          setForm(prev => ({
                            ...prev,
                            longForm: { yt: false, pod: false, live: false, none: !prev.longForm.none },
                          }));
                        } else {
                          setForm(prev => ({
                            ...prev,
                            longForm: {
                              ...prev.longForm,
                              [opt.key]: !prev.longForm[opt.key],
                              none: false,
                            },
                          }));
                        }
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
              <div className={styles.groupBlock}>
                <label>
                  Short-form library:
                  <OptionGroup
                    options={[
                      "Yes – It's large & consistently growing",
                      "Yes – It's decent but not consistent",
                      'Not really',
                    ]}
                    selected={form.shortLib}
                    onChange={val => updateField('shortLib', val)}
                  />
                </label>
                <label>
                  Average monthly revenue:
                  <OptionGroup
                    options={['$0-$10k', '$10k-$50k', '$50k-$100k', '$100k+']}
                    selected={form.revenue}
                    onChange={val => updateField('revenue', val)}
                  />
                </label>
              </div>
            </fieldset>
          );
        },
      },
      {
        id: 'assetsBusinessReferral',
        isComplete: () =>
          (form.hasExtraContent === 'Yes' || form.hasExtraContent === 'No') &&
          (referralUsername ||
            (form.isReferral === 'No' || (form.isReferral === 'Yes' && form.referredBy.trim() !== ''))),
        render: () => (
          <fieldset className={styles.step} key="assetsBusinessReferral">
            <legend>Final Details</legend>
            <label>
              Do you have extra content folders?<span className={styles.required}>*</span>
              <OptionGroup
                options={['Yes', 'No']}
                selected={form.hasExtraContent}
                onChange={val => updateField('hasExtraContent', val)}
              />
            </label>
            {form.hasExtraContent === 'Yes' && (
              <label>
                Extra content link:
                <input
                  className={styles.input}
                  placeholder="Dropbox/Google Drive URL"
                  value={form.extraContentLink}
                  onChange={e => updateField('extraContentLink', e.target.value.trim())}
                />
              </label>
            )}
            <div className={styles.formGrid}>
              <label>
                Business Name (optional):
                <input
                  className={styles.input}
                  placeholder="e.g. MyBrand Inc."
                  value={form.businessName}
                  onChange={e => updateField('businessName', e.target.value.trimStart())}
                />
              </label>
              <label>
                Business Email (optional):
                <input
                  type="email"
                  className={styles.input}
                  placeholder="e.g. info@mybrand.com"
                  value={form.businessEmail}
                  onChange={e => updateField('businessEmail', e.target.value.trim())}
                />
              </label>
            </div>
            {!referralUsername && (
              <div className={styles.groupBlock}>
                <label>
                  Was this a referral?<span className={styles.required}>*</span>
                  <OptionGroup
                    options={['Yes', 'No']}
                    selected={form.isReferral}
                    onChange={val => updateField('isReferral', val)}
                  />
                </label>
                {form.isReferral === 'Yes' && (
                  <div className={styles.referralField}>
                    <label>
                      Referred by:
                      <input
                        className={styles.input}
                        placeholder="Name of person who referred you"
                        value={form.referredBy}
                        onChange={e => updateField('referredBy', e.target.value.trimStart())}
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
          </fieldset>
        ),
      },
    ];
    return skipServiceStep ? steps.filter((s) => s.id !== 'services') : steps;
  }, [form, referralUsername, skipServiceStep, platformIconMap]);

  const focusedSteps = useMemo(() => {
    const longFormSelected =
      form.longForm.yt || form.longForm.pod || form.longForm.live || form.longForm.none;

    const steps = [
      {
        id: 'contactName',
        label: 'Name',
        isComplete: () => form.name.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="contactName">
            <legend>
              What&apos;s your name?<span className={styles.required}>*</span>
            </legend>
            <label>
              Full name
              <input
                className={styles.input}
                placeholder="Full Name"
                value={form.name}
                onChange={e => updateField('name', e.target.value.trimStart())}
              />
            </label>
          </fieldset>
        ),
      },
      {
        id: 'contactMethod',
        label: 'Contact',
        isComplete: () => form.phone.trim() !== '' || form.email.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="contactMethod">
            <legend>
              How should we contact you?<span className={styles.required}>*</span>
            </legend>
            <div className={styles.formGrid}>
              <label>
                Phone number
                <input
                  type="tel"
                  className={styles.input}
                  placeholder="e.g. 123-456-7890"
                  value={form.phone}
                  onChange={e => updateField('phone', e.target.value.trim())}
                />
              </label>
              <label>
                Email address
                <input
                  type="email"
                  className={styles.input}
                  placeholder="e.g. you@example.com"
                  value={form.email}
                  onChange={e => updateField('email', e.target.value.trim())}
                />
              </label>
            </div>
          </fieldset>
        ),
      },
      {
        id: 'services',
        label: 'Path',
        isComplete: () => form.services.facebook || form.services.snapchat,
        render: () => {
          const options = [
            {
              id: 'facebook',
              label: 'Facebook Page Management',
              description: 'Page growth and management track',
              icon: <SiFacebook size={24} />,
            },
            {
              id: 'snapchat',
              label: 'Snapchat Star Acquisition',
              description: 'Snap Star acquisition track',
              icon: <SiSnapchat size={24} />,
            },
          ];

          const handleServiceSelect = (serviceId) => {
            const newServices = { facebook: false, snapchat: false };
            newServices[serviceId] = true;
            setForm(prev => ({ ...prev, services: newServices }));
          };

          return (
            <fieldset className={styles.step} key="services">
              <legend>
                Which path are you applying for?<span className={styles.required}>*</span>
              </legend>
              <div className={styles.serviceOptions}>
                {options.map(opt => {
                  const isSelected = form.services[opt.id];
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      className={`${styles.serviceOption} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleServiceSelect(opt.id)}
                      aria-pressed={isSelected}
                    >
                      <div className={styles.serviceIcon}>{opt.icon}</div>
                      <div className={styles.serviceContent}>
                        <div className={styles.serviceLabel}>{opt.label}</div>
                        <div className={styles.serviceDescription}>{opt.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>
          );
        },
      },
      {
        id: 'hasFbPage',
        label: 'Page',
        isComplete: () => form.hasFbPage === 'Yes' || form.hasFbPage === 'No',
        render: () => (
          <fieldset className={styles.step} key="hasFbPage">
            <legend>
              Do you already have a Facebook Page?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.hasFbPage}
              onChange={val => updateField('hasFbPage', val)}
            />
          </fieldset>
        ),
      },
      {
        id: 'fbPageName',
        label: 'Page name',
        condition: () => form.hasFbPage === 'Yes',
        isComplete: () => form.fbPageName.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="fbPageName">
            <legend>
              What&apos;s the exact Facebook Page name?<span className={styles.required}>*</span>
            </legend>
            <label>
              Page name
              <input
                className={styles.input}
                placeholder="Exact Page Name"
                value={form.fbPageName}
                onChange={e => updateField('fbPageName', e.target.value.trimStart())}
              />
            </label>
          </fieldset>
        ),
      },
      {
        id: 'fbUsernames',
        label: 'Username',
        condition: () => form.hasFbPage === 'Yes',
        isComplete: () =>
          form.fbUsernames.length > 0 && form.fbUsernames.every(u => u.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="fbUsernames">
            <legend>
              What Facebook username should we try?<span className={styles.required}>*</span>
            </legend>
            <label className={styles.compactLabel}>Add backups if the first one is taken.</label>
            {form.fbUsernames.map((u, idx) => (
              <div key={idx} className={styles.platformRow}>
                <input
                  className={styles.inputHalf}
                  placeholder="facebook.com/username"
                  value={u}
                  onChange={e => updateFbUsername(idx, e.target.value.trim())}
                />
                {form.fbUsernames.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFbUsername(idx)}
                    className={styles.removeBtn}
                    aria-label="Remove this username"
                  >
                    x
                  </button>
                )}
              </div>
            ))}
            <button type="button" onClick={addFbUsername} className={styles.smallLink}>
              <span className={styles.plusIcon}>+</span>
              <span>Add another username</span>
            </button>
          </fieldset>
        ),
      },
      {
        id: 'contentSources',
        label: 'Sources',
        isComplete: () =>
          form.contentSources.length > 0 && form.contentSources.every(cs => cs.handle.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="contentSources">
            <legend>
              Where can we pull content from?<span className={styles.required}>*</span>
            </legend>
            <div className={styles.platformList}>
              {form.contentSources.map((entry, idx) => (
                <div key={idx} className={styles.platformRow}>
                  <div className={styles.platformIconContainer}>
                    {platformIconMap[entry.platform]}
                  </div>
                  <select
                    className={styles.platformSelect}
                    value={entry.platform}
                    onChange={e => updateContentSource(idx, 'platform', e.target.value)}
                  >
                    <option value="Instagram">Instagram</option>
                    <option value="TikTok">TikTok</option>
                    <option value="YouTube">YouTube</option>
                    <option value="Facebook">Facebook</option>
                    <option value="Other">Other</option>
                  </select>
                  <input
                    className={styles.inputInline}
                    placeholder={`${entry.platform} handle`}
                    value={entry.handle}
                    onChange={e => updateContentSource(idx, 'handle', e.target.value.trim())}
                  />
                  {form.contentSources.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeContentSource(idx)}
                      className={styles.removeBtn}
                      aria-label="Remove this platform entry"
                    >
                      x
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button type="button" onClick={addContentSource} className={styles.smallLink}>
              <span className={styles.plusIcon}>+</span>
              <span>Add another platform</span>
            </button>
          </fieldset>
        ),
      },
      {
        id: 'promoLink',
        label: 'Link',
        isComplete: () => form.promoLink.trim() !== '',
        render: () => (
          <fieldset className={styles.step} key="promoLink">
            <legend>
              What link should we promote?<span className={styles.required}>*</span>
            </legend>
            <label>
              Link to promote
              <input
                className={styles.input}
                placeholder="Full URL"
                value={form.promoLink}
                onChange={e => updateField('promoLink', e.target.value.trim())}
              />
            </label>
          </fieldset>
        ),
      },
      {
        id: 'autoResponse',
        label: 'Reply',
        isComplete: () => true,
        render: () => (
          <fieldset className={styles.step} key="autoResponse">
            <legend>Do you have an auto response?</legend>
            <label>
              Auto response (optional)
              <textarea
                className={styles.textarea}
                placeholder="Paste your auto-reply here"
                value={form.autoResponse}
                onChange={e => updateField('autoResponse', e.target.value)}
              />
            </label>
          </fieldset>
        ),
      },
      {
        id: 'longForm',
        label: 'Long-form',
        isComplete: () => longFormSelected,
        render: () => {
          const opts = [
            { key: 'yt', label: 'YouTube' },
            { key: 'pod', label: 'Podcasts' },
            { key: 'live', label: 'Live Streams' },
            { key: 'none', label: 'None of the Above' },
          ];
          return (
            <fieldset className={styles.step} key="longForm">
              <legend>
                Do you create long-form content?<span className={styles.required}>*</span>
              </legend>
              <div className={styles.optionGroupMulti}>
                {opts.map(opt => {
                  const isSelected = form.longForm[opt.key];
                  return (
                    <div
                      key={opt.key}
                      className={`${styles.toggleButton} ${isSelected ? styles.toggleSelected : ''}`}
                      onClick={() => {
                        if (opt.key === 'none') {
                          setForm(prev => ({
                            ...prev,
                            longForm: { yt: false, pod: false, live: false, none: !prev.longForm.none },
                          }));
                        } else {
                          setForm(prev => ({
                            ...prev,
                            longForm: {
                              ...prev.longForm,
                              [opt.key]: !prev.longForm[opt.key],
                              none: false,
                            },
                          }));
                        }
                      }}
                    >
                      {opt.label}
                    </div>
                  );
                })}
              </div>
            </fieldset>
          );
        },
      },
      {
        id: 'shortLib',
        label: 'Library',
        isComplete: () => form.shortLib !== '',
        render: () => (
          <fieldset className={styles.step} key="shortLib">
            <legend>
              How strong is your short-form library?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={[
                "Yes - It's large & consistently growing",
                "Yes - It's decent but not consistent",
                'Not really',
              ]}
              selected={form.shortLib}
              onChange={val => updateField('shortLib', val)}
            />
          </fieldset>
        ),
      },
      {
        id: 'revenue',
        label: 'Revenue',
        isComplete: () => form.revenue !== '',
        render: () => (
          <fieldset className={styles.step} key="revenue">
            <legend>
              What is your average monthly revenue?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['$0-$10k', '$10k-$50k', '$50k-$100k', '$100k+']}
              selected={form.revenue}
              onChange={val => updateField('revenue', val)}
            />
          </fieldset>
        ),
      },
      {
        id: 'extraContent',
        label: 'Assets',
        isComplete: () => form.hasExtraContent === 'Yes' || form.hasExtraContent === 'No',
        render: () => (
          <fieldset className={styles.step} key="extraContent">
            <legend>
              Do you have extra content folders?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.hasExtraContent}
              onChange={val => updateField('hasExtraContent', val)}
            />
            {form.hasExtraContent === 'Yes' && (
              <label>
                Extra content link
                <input
                  className={styles.input}
                  placeholder="Dropbox/Google Drive URL"
                  value={form.extraContentLink}
                  onChange={e => updateField('extraContentLink', e.target.value.trim())}
                />
              </label>
            )}
          </fieldset>
        ),
      },
      {
        id: 'business',
        label: 'Business',
        isComplete: () => true,
        render: () => (
          <fieldset className={styles.step} key="business">
            <legend>Any business details we should include?</legend>
            <div className={styles.formGrid}>
              <label>
                Business name (optional)
                <input
                  className={styles.input}
                  placeholder="e.g. MyBrand Inc."
                  value={form.businessName}
                  onChange={e => updateField('businessName', e.target.value.trimStart())}
                />
              </label>
              <label>
                Business email (optional)
                <input
                  type="email"
                  className={styles.input}
                  placeholder="e.g. info@mybrand.com"
                  value={form.businessEmail}
                  onChange={e => updateField('businessEmail', e.target.value.trim())}
                />
              </label>
            </div>
          </fieldset>
        ),
      },
      {
        id: 'referral',
        label: 'Referral',
        condition: () => !referralUsername,
        isComplete: () =>
          form.isReferral === 'No' || (form.isReferral === 'Yes' && form.referredBy.trim() !== ''),
        render: () => (
          <fieldset className={styles.step} key="referral">
            <legend>
              Was this a referral?<span className={styles.required}>*</span>
            </legend>
            <OptionGroup
              options={['Yes', 'No']}
              selected={form.isReferral}
              onChange={val => updateField('isReferral', val)}
            />
            {form.isReferral === 'Yes' && (
              <div className={styles.referralField}>
                <label>
                  Referred by
                  <input
                    className={styles.input}
                    placeholder="Name of person who referred you"
                    value={form.referredBy}
                    onChange={e => updateField('referredBy', e.target.value.trimStart())}
                  />
                </label>
              </div>
            )}
          </fieldset>
        ),
      },
    ];

    return skipServiceStep ? steps.filter((s) => s.id !== 'services') : steps;
  }, [form, referralUsername, skipServiceStep, platformIconMap]);

  // ─── Filter steps based on condition ─────────────────────────────────
  const visibleSteps = useMemo(
    () => (isEmbedded ? focusedSteps : allSteps).filter(step => !step.condition || step.condition()),
    [allSteps, focusedSteps, isEmbedded]
  );

  useEffect(() => {
    setStepIndex((i) => Math.min(i, Math.max(visibleSteps.length - 1, 0)));
  }, [visibleSteps.length]);

  // ─── Thank You Screen ────────────────────────────────────────────────
  if (submitted) {
    const thankYouCard = (
      <div className={styles.thankYouWrapper}>
        <div className={styles.thankYouContent}>
          <div className={styles.thankYouIcon}>✓</div>
          <h1 className={styles.thankYouTitle} id="onboard-success-title">
            Success!
          </h1>
          <p className={styles.thankYouMessage}>Form has been successfully submitted.</p>
          <div className={styles.welcomeSection}>
            <h2 className={styles.welcomeTitle}>Welcome to GrowYourOnlyFans!</h2>
            <p className={styles.welcomeMessage}>
              You&apos;re officially in. Our team will reach out shortly to start scaling your brand —
              let&apos;s build something massive together.
            </p>
          </div>
          <div className={styles.redirectSection}>
            {isEmbedded ? (
              <>
                <p className={styles.redirectMessage}>
                  You&apos;re all set. Return to the home panel anytime.
                </p>
                <button
                  type="button"
                  className={styles.redirectButton}
                  onClick={() => onClose?.()}
                >
                  Back to path selection
                </button>
              </>
            ) : (
              <>
                <p className={styles.redirectMessage}>
                  Redirecting to home page in <span className={styles.countdown}>{countdown}</span>{' '}
                  seconds
                </p>
                <button
                  type="button"
                  className={styles.redirectButton}
                  onClick={() => {
                    window.location.href = '/';
                  }}
                >
                  Click here to go home now
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );

    if (isEmbedded) {
      return <div className={`${styles.embeddedRoot} ${styles.embeddedSkin}`}>{thankYouCard}</div>;
    }
    if (isOverlay) {
      return (
        <div
          className={styles.overlayRoot}
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboard-success-title"
        >
          <div className={styles.overlayCard}>
            <div className={`${styles.container} ${styles.containerInOverlay}`}>{thankYouCard}</div>
          </div>
        </div>
      );
    }
    return <div className={styles.container}>{thankYouCard}</div>;
  }

  const total = visibleSteps.length;
  const current = visibleSteps[stepIndex];
  const currentLabel = current?.label || `Step ${stepIndex + 1}`;
  const isCurrentStepComplete = current.isComplete ? current.isComplete() : true;
  const progressPct = Math.round(((stepIndex + 1) / total) * 100);

  const rootClass = isEmbedded
    ? `${styles.embeddedRoot} ${styles.embeddedSkin}`
    : isOverlay
      ? `${styles.container} ${styles.containerInOverlay}`
      : styles.container;

  const goPrev = () => {
    if (isEmbedded && stepIndex === 0) {
      onClose?.();
      return;
    }
    setStepIndex((i) => Math.max(i - 1, 0));
  };
  const goNext = () => setStepIndex((i) => Math.min(i + 1, total - 1));

  const wizardInner = (
    <div className={rootClass}>
      {isEmbedded ? (
        <div className={styles.embeddedBody}>
          <div className={styles.embeddedShell} ref={embeddedTopRef}>
            <div className={styles.embeddedChrome}>
              <div className={styles.embeddedProgressTrack} aria-hidden="true">
                <span className={styles.embeddedProgressFill} style={{ width: `${progressPct}%` }} />
              </div>
              <div className={styles.embeddedDots} aria-hidden="true">
                {visibleSteps.map((_, i) => (
                  <span
                    key={i}
                    className={
                      i === stepIndex
                        ? `${styles.embeddedDot} ${styles.embeddedDotActive}`
                        : i < stepIndex
                          ? `${styles.embeddedDot} ${styles.embeddedDotDone}`
                          : styles.embeddedDot
                    }
                  />
                ))}
              </div>
              <span className={styles.embeddedStepCount} aria-live="polite">
                {currentLabel} | {stepIndex + 1} / {total}
              </span>
            </div>

            <div className={styles.embeddedStepOuter} key={`${current.id}-${stepIndex}`}>
              <div className={`${styles.stepWrapper} ${styles.stepWrapperEmbedded}`}>{current.render()}</div>
            </div>
          </div>

          <div className={styles.embeddedActionBar}>
            <div className={styles.embeddedActionButtons}>
              <button
                type="button"
                onClick={goPrev}
                disabled={stepIndex === 0 && !onClose}
                className={styles.embeddedSecondaryButton}
                aria-label={stepIndex === 0 ? 'Back to path selection' : 'Previous step'}
              >
                <FaChevronLeft aria-hidden />
                <span>Previous</span>
              </button>
              {stepIndex < total - 1 ? (
                <button
                  type="button"
                  onClick={goNext}
                  className={styles.embeddedPrimaryButton}
                  disabled={!isCurrentStepComplete}
                >
                  <span>Next</span>
                  <FaChevronRight aria-hidden />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={sending || !isCurrentStepComplete}
                  className={styles.embeddedPrimaryButton}
                >
                  <span>{sending ? 'Saving...' : 'Submit'}</span>
                  {!sending && <FaCheck aria-hidden />}
                </button>
              )}
            </div>
          </div>

          <div className={styles.embeddedMobileNav} style={{ display: 'none' }} aria-hidden="true">
            <button
              type="button"
              onClick={goPrev}
              disabled={stepIndex === 0}
              className={`${styles.button} ${stepIndex === 0 ? styles.disabled : ''}`}
            >
              Previous
            </button>
            {stepIndex < total - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className={styles.buttonPrimary}
                disabled={!isCurrentStepComplete}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={sending || !isCurrentStepComplete}
                className={`${styles.buttonPrimary} ${sending ? styles.disabled : ''}`}
              >
                {sending ? 'Saving…' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      ) : (
        <>
          {isOverlay && (
            <div className={styles.wizardHeader}>
              <div>
                <p id="onboard-wiz-title" className={styles.wizardKicker}>
                  Creator onboarding
                </p>
                <p className={styles.wizardStepLabel}>
                  Step {stepIndex + 1} of {total}
                </p>
              </div>
              {onClose && (
                <button type="button" className={styles.overlayClose} onClick={onClose} aria-label="Close form">
                  ×
                </button>
              )}
            </div>
          )}

          <div className={styles.progressWrapper}>
            <div className={styles.progressTrack}>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }} />
            </div>
            {variant === 'page' && (
              <div className={styles.progressText}>
                Step {stepIndex + 1} of {total}
              </div>
            )}
          </div>

          <div className={styles.stepWrapper} key={current.id}>
            {current.render()}
          </div>

          <div className={styles.navButtons}>
            <button
              type="button"
              onClick={goPrev}
              disabled={stepIndex === 0}
              className={`${styles.button} ${stepIndex === 0 ? styles.disabled : ''}`}
            >
              Previous
            </button>

            {stepIndex < total - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className={styles.buttonPrimary}
                disabled={!isCurrentStepComplete}
              >
                Next
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={sending || !isCurrentStepComplete}
                className={`${styles.buttonPrimary} ${sending ? styles.disabled : ''}`}
              >
                {sending ? 'Saving…' : 'Save'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  if (isEmbedded) {
    return wizardInner;
  }
  if (isOverlay) {
    return (
      <div className={styles.overlayRoot} role="dialog" aria-modal="true" aria-labelledby="onboard-wiz-title">
        <div className={styles.overlayCard}>{wizardInner}</div>
      </div>
    );
  }
  return wizardInner;
}
